// ===== O texto do evento de timeline: da tela para o banco e de volta =====
//
// POR QUE ISTO EXISTE: os eventos da timeline são montados como JSX no store --
// 23 das 27 chamadas de `appendTimeline` passam coisas como
// `<>Estágio movido <strong>PROPOSTA → GANHO</strong>.</>`.
//
// JSX não vai para o banco. E a coluna `timeline_eventos.texto` é `text not
// null`, então ou se perde a ênfase, ou se perde o evento.
//
// A saída é um marcador simples: `**assim**` no texto guardado, reconstruído
// como `<strong>` na leitura. Não é markdown -- é uma convenção de uma regra só,
// e por isso não precisa de biblioteca nem abre porta para HTML injetado.
//
// A ALTERNATIVA QUE NÃO SEGUI era reescrever as 27 chamadas para string. Seria
// mais limpo no fim, mas são 27 edições manuais num arquivo de 2.700 linhas,
// cada uma uma chance de trocar uma palavra sem ninguém notar -- e o texto do
// evento é justamente o que ninguém confere depois.

import React from 'react';

const ENFASE = new Set(['strong', 'b', 'em']);

/**
 * Converte o conteúdo de um evento em texto plano, marcando a ênfase.
 *
 * Aceita string (passa direto), número, array e elemento React. Qualquer outra
 * coisa vira string vazia em vez de `[object Object]` -- que é o que apareceria
 * na timeline do vendedor.
 */
export function paraTexto(no) {
  if (no == null || no === false || no === true) return '';
  if (typeof no === 'string') return no;
  if (typeof no === 'number') return String(no);
  if (Array.isArray(no)) return no.map(paraTexto).join('');

  // Elemento React: `props.children` é o conteúdo; `type` diz se é ênfase.
  const tipo = no?.type;
  const filhos = no?.props?.children;
  if (filhos === undefined) return '';

  const dentro = paraTexto(filhos);
  if (typeof tipo === 'string' && ENFASE.has(tipo)) {
    // Ênfase vazia não vira `****`, que apareceria como literal na tela.
    return dentro ? `**${dentro}**` : '';
  }
  return dentro;
}

/**
 * Reconstrói a ênfase para exibição.
 *
 * Devolve um array de nós React. O texto entre `**` vira `<strong>`; o resto sai
 * como está. Nada é interpretado como HTML -- o conteúdo entra como filho de
 * elemento, então mesmo um evento com `<script>` no texto aparece literal.
 */
export function paraElementos(texto) {
  const s = String(texto ?? '');
  if (!s) return [];
  const partes = s.split('**');
  return partes.map((parte, i) => {
    if (!parte) return null;
    // Índice ímpar = estava entre marcadores. Marcador sem par (número ímpar de
    // `**`) deixa o último pedaço fora da ênfase, que é melhor que engolir texto.
    return i % 2 === 1
      ? React.createElement('strong', { key: i }, parte)
      : React.createElement(React.Fragment, { key: i }, parte);
  }).filter(Boolean);
}

/** Um evento da tela, pronto para a coluna. */
export function eventoParaBanco(negocioId, evento, autorUserId = null) {
  return {
    negocio_id: negocioId,
    // 'note', nao 'nota': o enum `timeline_tipo` (0002) so tem 'note', e o
    // padrao antigo fazia o banco recusar todo evento sem tipo.
    tipo: evento?.tipo || 'note',
    ator_tipo: evento?.autorTipo === 'agente' ? 'ia' : 'humano',
    // `ator_user_id` só quando é gente nossa: a coluna aponta para `profiles`, e
    // um id de agente ali quebraria a chave estrangeira.
    ator_user_id: evento?.autorTipo === 'agente' ? null : autorUserId,
    autor_nome: evento?.autor || null,
    texto: paraTexto(evento?.texto),
    meta: evento?.detail ? { detail: String(evento.detail) } : {},
  };
}

/** Uma linha do banco, pronta para a tela. */
export function eventoDoBanco(linha) {
  return {
    id: linha?.id,
    tipo: linha?.tipo || 'nota',
    autorTipo: linha?.ator_tipo === 'ia' ? 'agente' : 'humano',
    autor: linha?.autor_nome || '—',
    // A tela renderiza `{e.texto}`; devolver os elementos aqui mantém a ênfase
    // sem exigir que cada lugar que exibe timeline saiba do marcador.
    texto: paraElementos(linha?.texto),
    detail: linha?.meta?.detail || null,
    ocorreuEm: linha?.ocorreu_em || null,
  };
}
